<template>
  <button
    type="button"
    :disabled="disabled || submittingPrepend"
    :class="classes"
    @click.stop="$emit('click')"
    @mousedown.stop
  >
    <div v-if="submittingPrepend" class="flex justify-center mr-2">
      <font-awesome-icon
        icon="fa-solid fa-circle-notch"
        class="h-5 w-5"
        spin size="2xl"
      />
    </div>
    <div v-if="faIcon && iconFirst" class="flex items-center">
      <font-awesome-icon
        :icon="icon"
        :size="iconSize !== 'md' ? iconSize : null"
        :class="[animationClass, label ? 'mr-2' : 'm-1']"
      />
    </div>
    {{ label }}
    <div v-if="faIcon && !iconFirst" class="flex items-center">
      <font-awesome-icon
        :icon="icon"
        :size="iconSize !== 'md' ? iconSize : null"
        :class="[animationClass, label && 'ml-1.5']"
      />
    </div>
  </button>
</template>

<script>
export default {
  name: "VButton",
  props: {
    label: {
      type: String,
      required: false,
    },
    block: {
      type: Boolean,
      default: false,
      required: false,
    },
    disabled: {
      type: Boolean,
      default: false,
      required: false,
    },
    waiting: {
      type: Boolean,
      default: false
    },
    outlined: {
      type: Boolean,
      default: false,
      required: false,
    },
    uppercase: {
      type: Boolean,
      default: false,
      required: false,
    },
    color: {
      type: String,
      default: "primary",
      required: false,
    },
    size: {
      type: String,
      default: "md",
      required: false,
    },
    icon: {
      type: String,
      required: false,
    },
    iconSize: {
      type: String,
      default: "md",
    },
    iconAnimation: {
      type: String,
      required: false,
    },
    iconFirst: {
      type: Boolean,
      default: false
    },
    submittingPrepend: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: ["click", "blur"],
  computed: {
    disabledButton () {
      return this.disabled || this.submittingPrepend
    },
    classes() {
      return {
        "flex justify-center items-center rounded transition-all duration-200 font-bold select-none": true,
        "px-4 py-2": this.size !== "xs",
        "p-1": this.size === "xs",
        "w-full": this.block,
        "text-white bg-primary hover:bg-primary-400 focus:bg-primary-400":
          this.color === "primary" && !this.outlined && !this.disabledButton && !this.waiting,
        "text-primary bg-white hover:bg-primary hover:text-white focus:bg-primary focus:text-white border-2 border-primary":
          this.color === "primary" && this.outlined && !this.disabledButton && !this.waiting,
        "text-white bg-secondary hover:bg-secondary-600 focus:bg-secondary-600":
          this.color === "secondary" && !this.outlined && !this.disabledButton && !this.waiting,
        "text-secondary bg-white hover:bg-secondary hover:text-white focus:bg-secondary focus:text-white border-2 border-secondary":
          this.color === "secondary" && this.outlined && !this.disabledButton && !this.waiting,
        "bg-accent hover:bg-opacity-80 focus:bg-opacity-80": this.color === "accent" && !this.outlined && !this.disabledButton && !this.waiting,
        "text-accent bg-white hover:bg-accent hover:text-white focus:bg-accent focus:text-white border-2 border-accent":
          this.color === "accent" && this.outlined && !this.disabledButton && !this.waiting,
        "bg-info hover:bg-opacity-80 focus:bg-opacity-80": this.color === "info" && !this.outlined && !this.disabledButton && !this.waiting,
        "text-info-dark bg-white hover:bg-info-dark focus:bg-info-dark hover:text-white focus:text-white border-2 border-info-dark dark:bg-blue-800/20 dark:text-info dark:hover:bg-info-dark/60":
          this.color === "info" && this.outlined && !this.disabledButton && !this.waiting,
        "text-white bg-success hover:bg-opacity-80 focus:bg-opacity-80": this.color === "success" && !this.outlined && !this.disabledButton && !this.waiting,
        "text-success bg-white hover:bg-success hover:text-white focus:bg-success focus:text-white dark:bg-green-500/10 dark:text-green-50 dark:hover:bg-green-600/20 dark:hover:border-green-300 dark:focus:bg-green-600/20 dark:focus:border-green-300 border-2 border-success":
          this.color === "success" && this.outlined && !this.disabledButton && !this.waiting,
        "text-white bg-caution hover:bg-opacity-80 focus:bg-opacity-80": this.color === "caution" && !this.outlined && !this.disabledButton && !this.waiting,
        "text-caution bg-white hover:bg-caution hover:text-white focus:bg-caution focus:text-white border-2 border-caution":
          this.color === "caution" && this.outlined && !this.disabledButton && !this.waiting,
        "text-white bg-warning hover:bg-opacity-80 focus:bg-opacity-80": this.color === "warning" && !this.outlined && !this.disabledButton && !this.waiting,
        "text-warning bg-white hover:bg-warning hover:text-white focus:bg-warning focus:text-white border-2 border-warning":
          this.color === "warning" && this.outlined && !this.disabledButton && !this.waiting,
        "text-white bg-warning-dark hover:bg-opacity-80 focus:bg-opacity-80": this.color === "warning-dark" && !this.outlined && !this.disabledButton && !this.waiting,
        "text-warning bg-white hover:bg-warning-dark hover:text-white focus:bg-warning-dark focus:text-white border-2 border-warning-dark":
          this.color === "warning-dark" && this.outlined && !this.disabledButton && !this.waiting,
        "text-white bg-danger hover:bg-opacity-80 focus:bg-opacity-80": this.color === "danger" && !this.outlined && !this.disabledButton && !this.waiting,
        "text-danger bg-white hover:bg-danger hover:text-white focus:bg-danger focus:text-white border-2 border-danger":
          this.color === "danger" && this.outlined && !this.disabledButton && !this.waiting,
        "bg-white dark:bg-black/90 hover:bg-gray-100 focus:bg-gray-100": this.color === "white" && !this.outlined && !this.disabledButton && !this.waiting,
        "bg-white dark:bg-black/90 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 focus:bg-gray-100 dark:hover:bg-gray-700 dark:focus:bg-gray-700 border-2 border-gray-300 dark:border-gray-800 hover:border-gray-700 dark:hover:border-gray-500 focus:border-gray-700 dark:focus:border-gray-500": this.color === "white" && this.outlined && !this.disabledButton && !this.waiting,
        "text-white bg-black hover:bg-opacity-80 focus:bg-opacity-80": this.color === "black" && !this.outlined && !this.disabledButton && !this.waiting,
        "text-black bg-white hover:bg-black hover:text-white focus:bg-black focus:text-white border-2 border-black": this.color === "black" && this.outlined && !this.disabledButton && !this.waiting,
        "text-white bg-gray-500 hover:bg-gray-500/80 focus:bg-gray-500/80 dark:bg-gray-800 dark:hover:bg-gray-600 dark:focus:bg-gray-600": this.color === "gray" && !this.outlined && !this.disabledButton && !this.waiting,
        "text-black bg-gray-500 hover:bg-black hover:text-white focus:bg-black focus:text-white border-2 border-black": this.color === "gray" && this.outlined && !this.disabledButton && !this.waiting,
        "text-white dark:text-gray-200/40 bg-gray-400 dark:bg-gray-800 cursor-not-allowed dark:border dark:border-gray-600/60": !this.outlined && this.disabledButton,
        "text-gray-400 dark:text-gray-500 bg-white dark:bg-white/10 border-2 border-gray-400 dark:border-gray-600 cursor-not-allowed": this.outlined && this.disabledButton,
        "text-white dark:text-gray-200/40 bg-gray-400 dark:bg-gray-800 cursor-wait": !this.outlined && this.waiting,
        "text-gray-400 dark:text-gray-500 bg-white dark:bg-white/10 border-2 border-gray-400 dark:border-gray-600 cursor-wait": this.outlined && this.waiting,
        "text-xs": this.size === "xs",
        "text-sm": this.size === "sm",
        "text-lg px-6 py-2": this.size === "lg",
        "text-xl px-8 py-2": this.size === "xl",
        "uppercase": this.uppercase,
      };
    },
    animationClass() {
      return {
        "animate-spin transform rotate-180": this.iconAnimation === 'spin',
        "animate-reverse-spin": this.iconAnimation === "spin-reverse"
      }
    },
    heroIcon() {
      return this.icon && this.icon.slice(0, 2) !== "fa"
    },
    faIcon() {
      return this.icon && this.icon.slice(0, 2) === "fa"
    },
  },
};
</script>

<style scoped>
/* .bg-info-dark {
  background-color: white;
}
.bg-info-dark:hover, .bg-info-dark:focus  {
  background-color: #2082b3;
} */
/* .text-info-dark {
  color: #2082b3;
}
.border-info-dark {
  border-color: #2082b3;
} */
/* ── VButton frosted glass (dark mode) ─────────── */
/* Primary filled */
[data-theme="dark"] .bg-primary {
  background: rgba(0, 146, 191, 0.30) !important;
  /* backdrop-filter: blur(12px); */
  /* -webkit-backdrop-filter: blur(12px); */
  border: 1px solid rgba(0, 188, 245, 0.30) !important;
  box-shadow: 0 2px 8px rgba(0, 146, 191, 0.25);
}

[data-theme="dark"] .bg-primary:hover,
[data-theme="dark"] .hover\:bg-primary-400:hover {
  background: rgba(0, 146, 191, 0.5) !important;
  border-color: rgba(0, 188, 245, 0.75) !important;
  box-shadow: 0 4px 20px rgba(0, 146, 191, 0.15);
}

/* Primary outlined */
[data-theme="dark"] .border-primary {
  border-color: rgba(0, 188, 245, 0.35) !important;
}

[data-theme="dark"] .border-primary.bg-white {
  background: rgba(0, 146, 191, 0.08) !important;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}
[data-theme="dark"] .border-primary.bg-white:hover {
  border-color: rgba(143, 204, 223, 0.75) !important;
}

/* Secondary filled */
[data-theme="dark"] button.bg-secondary,
[data-theme="dark"] a.bg-secondary {
  background: rgba(58, 85, 158, 0.35) !important;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(106, 131, 200, 0.30) !important;
  box-shadow: 0 4px 20px rgba(58, 85, 158, 0.15);
}

[data-theme="dark"] button.bg-secondary:hover,
[data-theme="dark"] a.bg-secondary:hover {
  background: rgba(58, 85, 158, 0.599) !important;
  border-color: rgba(138, 161, 222, 0.5) !important;
}

/* Secondary outlined */
[data-theme="dark"] .border-secondary {
  border-color: rgba(106, 131, 200, 0.35) !important;
}

[data-theme="dark"] .border-secondary.bg-white {
  background: rgba(58, 85, 158, 0.08) !important;
  backdrop-filter: blur(12px);
  color: #a6b5de;
  -webkit-backdrop-filter: blur(12px);
}

[data-theme="dark"] .border-secondary.bg-white:hover {
  background: rgba(79, 110, 194, 0.287) !important;
  border-color: rgba(106, 131, 200, 0.713) !important;
  color: #f0f4ff;
}

/* Success */
[data-theme="dark"] .bg-success {
  background: rgba(16, 185, 129, 0.35) !important;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(16, 185, 129, 0.30) !important;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.25);
}

[data-theme="dark"] .bg-success:hover {
  background: rgba(16, 185, 129, 0.513) !important;
  border-color: rgba(45, 222, 163, 0.572) !important;
}

/* Danger */
[data-theme="dark"] .bg-danger {
  background: rgba(239, 68, 68, 0.614) !important;
  backdrop-filter: blur(12px);
  box-shadow: 0 2px 8px #ef444440;;
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(239, 68, 68, 0.824) !important;
}

[data-theme="dark"] .bg-danger:hover {
  background: rgba(239, 68, 68, 0.756) !important;
  border-color: rgba(255, 192, 192, 0.5) !important;
}

/* Danger outlined */
[data-theme="dark"] .border-danger {
  background: rgba(239, 68, 68, 0.152) !important;
  backdrop-filter: blur(12px);
  box-shadow: 0 2px 8px #ef444416;;
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(239, 68, 68, 0.824) !important;
}

[data-theme="dark"] .border-danger:hover {
  background: rgba(239, 68, 68, 0.756) !important;
  border-color: rgba(255, 192, 192, 0.5) !important;
}

/* Warning */
[data-theme="dark"] .bg-warning {
  background: rgba(250, 191, 107, 0.20) !important;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid rgba(250, 191, 107, 0.30) !important;
}

[data-theme="dark"] .bg-warning:hover {
  background: rgba(250, 191, 107, 0.35) !important;
  border-color: rgba(250, 191, 107, 0.50) !important;
}

/* Info */
[data-theme=dark] .bg-info {
    background-color: #0092bf40;
}
[data-theme=dark] .bg-info:hover {
    background-color: #0092bf59;
}

/* ── Status/type chips (bright bg → muted glass) ── */
/* Preventive chip: bg-primary-15 */
[data-theme="dark"] .bg-primary-15 {
  background: rgba(0, 146, 191, 0.15) !important;
  color: rgba(0, 188, 245, 0.85) !important;
}

/* Scheduled chip: bg-indigo-100 */
[data-theme="dark"] .bg-indigo-100 {
  background: rgba(99, 102, 241, 0.15) !important;
  color: rgba(165, 180, 252, 0.85) !important;
}

/* Active chip: bg-green-100 */
[data-theme="dark"] .bg-green-100 {
  background: rgba(16, 185, 129, 0.15) !important;
  color: rgba(110, 231, 183, 0.85) !important;
}

/* Completed chip: bg-primary-50 */
[data-theme="dark"] .bg-primary-50 {
  background: rgba(0, 146, 191, 0.12) !important;
  color: rgba(0, 188, 245, 0.75) !important;
}

/* Secondary-50 chip (corrective type) */
[data-theme="dark"] .bg-secondary-50 {
  background: rgba(58, 85, 158, 0.15) !important;
  color: rgba(166, 181, 222, 0.85) !important;
}

/* ══════════════════════════════════════════════════
   LIGHT MODE POLISH
   ══════════════════════════════════════════════════ */

/* ── VButton light mode depth ──────────────────── */
/* Primary filled — subtle gradient + shadow */
button.bg-primary,
a.bg-primary {
  /* background: linear-gradient(135deg, #00ace0, #0092bf) !important; */
  transition: all 0.2s ease;
}

button.bg-primary:hover,
a.bg-primary:hover {
  /* background: linear-gradient(135deg, #00bcf5, #00ace0) !important; */
  transform: translateY(-0.2px);
}

button.bg-primary:active,
a.bg-primary:active {
  transform: translateY(0);
  /* box-shadow: 0 1px 4px rgba(0, 146, 191, 0.20); */
}

/* Secondary filled — subtle gradient */
button.bg-secondary,
a.bg-secondary {
  /* background: linear-gradient(135deg, #4c6abd, #3a559e) !important; */
  /* box-shadow: 0 2px 8px rgba(58, 85, 158, 0.25); */
  transition: all 0.2s ease;
}

button.bg-secondary:hover,
a.bg-secondary:hover {
  /* background: linear-gradient(135deg, #5a78cb, #4c6abd) !important; */
  /* box-shadow: 0 4px 16px rgba(58, 85, 158, 0.30); */
  transform: translateY(-1px);
}

/* Success filled */
button.bg-success,
a.bg-success {
  /* background: linear-gradient(135deg, #34d399, #10b981) !important; */
  /* box-shadow: 0 2px 8px rgba(16, 185, 129, 0.25); */
  transition: all 0.2s ease;
}

button.bg-success:hover,
a.bg-success:hover {
  /* box-shadow: 0 4px 16px rgba(16, 185, 129, 0.30); */
  transform: translateY(-0.2px);
}

/* Danger filled */
button.bg-danger,
a.bg-danger {
  /* background: linear-gradient(135deg, #f87171, #ef4444) !important; */
  /* box-shadow: 0 2px 8px rgba(239, 68, 68, 0.25); */
  transition: all 0.2s ease;
}

button.bg-danger:hover,
a.bg-danger:hover {
  /* box-shadow: 0 4px 16px rgba(239, 68, 68, 0.30); */
  transform: translateY(-0.2px);
}

/* ── Site-Track dashboard stat cards (light) ───── */
.bg-secondary-400 {
  /* background: linear-gradient(135deg, #5a78cb, #4c6abd) !important; */
  /* box-shadow: 0 2px 8px rgba(58, 85, 158, 0.20); */
  transition: all 0.2s ease;
}

.bg-secondary-400:hover {
  /* box-shadow: 0 4px 16px rgba(58, 85, 158, 0.30); */
  transform: translateY(-0.2px);
}
</style>
